/**
 * Configurable header menu + welcome splash contracts.
 *
 * Built-in menu entries store only a stable key. Their behavior and dynamic
 * visibility remain owned by Header/runtime code, so an admin configuration
 * can never bypass authentication or route guards.
 */

export type MenuSurface = 'main' | 'account' | 'mobile';
export type MenuRoute = 'wizard' | 'shared' | 'account' | 'admin' | 'other';

export type BuiltinMenuKey =
  | 'reload'
  | 'create-estimate'
  | 'new-from-template'
  | 'load-config'
  | 'save-config'
  | 'trusscore-link'
  | 'tutorial'
  | 'about'
  | 'sign-in'
  | 'sign-up'
  | 'my-estimates'
  | 'my-information'
  | 'account-security'
  | 'admin'
  | 'sign-out';

export interface MenuVisibility {
  wizard?: boolean;
  shared?: boolean;
  account?: boolean;
  admin?: boolean;
  other?: boolean;
}

export type SiteMenuEntry =
  | { id: string; type: 'builtin'; builtin: BuiltinMenuKey; label?: string; enabled?: boolean }
  | { id: string; type: 'link'; label: string; url: string; target: '_self' | '_blank'; visibility?: MenuVisibility; enabled?: boolean }
  | { id: string; type: 'divider'; enabled?: boolean };

export interface SiteMenuConfig {
  version: 1;
  entries: SiteMenuEntry[];
}

export interface SiteSplashConfig {
  version: 1;
  enabled: boolean;
  /** Sanitized rich HTML rendered between the optional image and CTA. */
  contentHtml: string;
  imageUrl?: string;
  imageAlt?: string;
  ctaLabel?: string;
  ctaUrl?: string;
  backgroundColor?: string;
  presentation: 'classic-trusscore' | 'brand-card';
}

export interface SiteExperienceConfig {
  menu: SiteMenuConfig;
  splash: SiteSplashConfig;
}

export interface MenuRuntimeState {
  route: MenuRoute;
  surface: MenuSurface;
  accountsUiOn: boolean;
  isAuthenticated: boolean;
  isAdminAuthenticated: boolean;
  hasSharedView: boolean;
  tutorialAvailable: boolean;
  splashAvailable: boolean;
}

export interface BuiltinMenuMeta {
  key: BuiltinMenuKey;
  /**
   * English label. Still the source of truth for the admin site-content editor,
   * which lists these by name and is English by scope.
   */
  label: string;
  /**
   * i18n key the header renders instead. An admin can override the label per
   * retailer via menu_json, and that override is database text belonging to the
   * Phase 3 content overlay — it wins over both of these.
   */
  labelKey: string;
  icon: string;
  /** Admin-facing explanation shown in the site-content editor. English. */
  description: string;
  surfaces: MenuSurface[];
  condition: (state: MenuRuntimeState) => boolean;
  conditionLabel?: string;
}

const nonAdmin = (s: MenuRuntimeState) => s.route !== 'admin';
const authOn = (s: MenuRuntimeState) => s.accountsUiOn;

export const BUILTIN_MENU_REGISTRY: BuiltinMenuMeta[] = [
  { key: 'reload', label: 'Reload / Start Over', labelKey: 'common:menu.reload', icon: 'refresh-ccw', description: 'Reset the current estimate and return to step one.', surfaces: ['main', 'mobile'], condition: nonAdmin, conditionLabel: 'Hidden in admin' },
  { key: 'create-estimate', label: 'Create a New Estimate', labelKey: 'common:menu.create-estimate', icon: 'plus', description: 'Leave admin and start a new estimate.', surfaces: ['main', 'mobile'], condition: s => s.route === 'admin', conditionLabel: 'Admin only' },
  { key: 'new-from-template', label: 'New Estimate from this template', labelKey: 'common:menu.new-from-template', icon: 'copy', description: 'Copy the currently viewed shared estimate configuration.', surfaces: ['main', 'mobile'], condition: s => s.route === 'shared' && s.hasSharedView, conditionLabel: 'Loaded shared estimate only' },
  { key: 'load-config', label: 'Load a Local Config', labelKey: 'common:menu.load-config', icon: 'upload', description: 'Load a locally saved estimator configuration.', surfaces: ['main', 'mobile'], condition: nonAdmin, conditionLabel: 'Hidden in admin' },
  { key: 'save-config', label: 'Save Current Config', labelKey: 'common:menu.save-config', icon: 'download', description: 'Save the current or shared estimate configuration.', surfaces: ['main', 'mobile'], condition: nonAdmin, conditionLabel: 'Hidden in admin' },
  { key: 'trusscore-link', label: 'Go to Trusscore.com', labelKey: 'common:menu.trusscore-link', icon: 'external-link', description: 'Open the Trusscore website.', surfaces: ['main', 'mobile'], condition: () => true },
  { key: 'tutorial', label: 'Tutorial Video', labelKey: 'common:menu.tutorial', icon: 'video', description: 'Open the configured tutorial video.', surfaces: ['main', 'mobile'], condition: s => s.route !== 'admin' && s.tutorialAvailable, conditionLabel: 'Requires tutorial URL; hidden in admin' },
  { key: 'about', label: 'About', labelKey: 'common:menu.about', icon: 'info', description: 'Open the welcome/about splash.', surfaces: ['main', 'mobile'], condition: s => s.splashAvailable, conditionLabel: 'Requires enabled splash' },
  { key: 'sign-in', label: 'Sign in', labelKey: 'common:menu.sign-in', icon: 'log-in', description: 'Open the configured sign-in flow.', surfaces: ['account', 'mobile'], condition: s => authOn(s) && !s.isAuthenticated, conditionLabel: 'Accounts on; signed out' },
  { key: 'sign-up', label: 'Create an account', labelKey: 'common:menu.sign-up', icon: 'user-plus', description: 'Open the configured account-registration flow.', surfaces: ['account', 'mobile'], condition: s => authOn(s) && !s.isAuthenticated, conditionLabel: 'Accounts on; signed out' },
  { key: 'my-estimates', label: 'My estimates', labelKey: 'common:menu.my-estimates', icon: 'file-pen', description: 'Open the signed-in user’s estimates.', surfaces: ['account', 'mobile'], condition: s => authOn(s) && s.isAuthenticated, conditionLabel: 'Accounts on; signed in' },
  { key: 'my-information', label: 'My information', labelKey: 'common:menu.my-information', icon: 'user-pen', description: 'Open the signed-in user’s profile.', surfaces: ['account', 'mobile'], condition: s => authOn(s) && s.isAuthenticated, conditionLabel: 'Accounts on; signed in' },
  { key: 'account-security', label: 'Password & sign-in', labelKey: 'common:menu.account-security', icon: 'lock', description: 'Open account security settings.', surfaces: ['account', 'mobile'], condition: s => authOn(s) && s.isAuthenticated, conditionLabel: 'Accounts on; signed in' },
  { key: 'admin', label: 'Admin', labelKey: 'common:menu.admin', icon: 'shield', description: 'Open the administrator area.', surfaces: ['account', 'mobile'], condition: s => authOn(s) && s.isAuthenticated && s.isAdminAuthenticated, conditionLabel: 'Signed-in administrator only' },
  { key: 'sign-out', label: 'Sign out', labelKey: 'common:menu.sign-out', icon: 'log-out', description: 'End the current account session.', surfaces: ['account', 'mobile'], condition: s => authOn(s) && s.isAuthenticated, conditionLabel: 'Accounts on; signed in' },
];

export const DEFAULT_SITE_MENU: SiteMenuConfig = {
  version: 1,
  entries: [
    { id: 'builtin-reload', type: 'builtin', builtin: 'reload' },
    { id: 'builtin-create-estimate', type: 'builtin', builtin: 'create-estimate' },
    { id: 'builtin-new-from-template', type: 'builtin', builtin: 'new-from-template' },
    { id: 'builtin-load-config', type: 'builtin', builtin: 'load-config' },
    { id: 'builtin-save-config', type: 'builtin', builtin: 'save-config' },
    { id: 'divider-main-links', type: 'divider' },
    { id: 'builtin-trusscore-link', type: 'builtin', builtin: 'trusscore-link' },
    { id: 'builtin-tutorial', type: 'builtin', builtin: 'tutorial' },
    { id: 'divider-about', type: 'divider' },
    { id: 'builtin-about', type: 'builtin', builtin: 'about' },
    { id: 'divider-account', type: 'divider' },
    { id: 'builtin-sign-in', type: 'builtin', builtin: 'sign-in' },
    { id: 'builtin-sign-up', type: 'builtin', builtin: 'sign-up' },
    { id: 'builtin-my-estimates', type: 'builtin', builtin: 'my-estimates' },
    { id: 'builtin-my-information', type: 'builtin', builtin: 'my-information' },
    { id: 'builtin-account-security', type: 'builtin', builtin: 'account-security' },
    { id: 'builtin-admin', type: 'builtin', builtin: 'admin' },
    { id: 'builtin-sign-out', type: 'builtin', builtin: 'sign-out' },
  ],
};

export const DEFAULT_SITE_SPLASH: SiteSplashConfig = {
  version: 1,
  enabled: true,
  presentation: 'classic-trusscore',
  imageUrl: '/images/trusscore-colour-light.svg',
  imageAlt: 'Trusscore',
  contentHtml: '<h1>Cost &amp; Materials Estimator</h1><p><strong>Calculate your Trusscore wall and ceiling material requirements</strong></p>',
};

export const DEFAULT_SITE_EXPERIENCE: SiteExperienceConfig = {
  menu: DEFAULT_SITE_MENU,
  splash: DEFAULT_SITE_SPLASH,
};

export function getBuiltinMenuMeta(key: BuiltinMenuKey): BuiltinMenuMeta | undefined {
  return BUILTIN_MENU_REGISTRY.find(item => item.key === key);
}

function routeAllowed(visibility: MenuVisibility | undefined, route: MenuRoute): boolean {
  if (!visibility || Object.keys(visibility).length === 0) return true;
  return visibility[route] !== false;
}

/** Resolve dynamic entries, then remove leading/trailing/consecutive dividers. */
export function resolveSiteMenu(config: SiteMenuConfig, state: MenuRuntimeState): SiteMenuEntry[] {
  const visible = config.entries.filter(entry => {
    if (entry.enabled === false) return false;
    if (entry.type === 'divider') return true;
    if (entry.type === 'link') {
      return state.surface !== 'account' && routeAllowed(entry.visibility, state.route);
    }
    const meta = getBuiltinMenuMeta(entry.builtin);
    return !!meta && meta.surfaces.includes(state.surface) && meta.condition(state);
  });

  const clean: SiteMenuEntry[] = [];
  for (const entry of visible) {
    if (entry.type === 'divider' && (clean.length === 0 || clean[clean.length - 1].type === 'divider')) continue;
    clean.push(entry);
  }
  while (clean[clean.length - 1]?.type === 'divider') clean.pop();
  return clean;
}

export function cloneSiteExperience<T>(value: T): T {
  return JSON.parse(JSON.stringify(value));
}
